import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string[];
    image?: string;
    url?: string;
    type?: string;
    structuredData?: Record<string, any>;
}

const SEO: React.FC<SEOProps> = ({
    title = 'SightTrail Safaris | Global East African Expeditions',
    description = 'SightTrail Safaris offers premium, authentic East African safari experiences. Explore Kenya, Tanzania, and Rwanda with our professional safari architects. Witness the Great Migration, the Big Five, and diverse landscapes.',
    keywords = [
        'safari', 'east africa safari', 'kenya safari', 'tanzania safari', 'professional guides', 'african wildlife',
        'masai mara', 'serengeti', 'luxury safari', 'family safari', 'great migration',
        'big five', 'authentic safari', 'bespoke african tours', 'photography safari', 'ngorongoro'
    ],
    image = '/SightTrails_Safaris_Logo_3-removebg-preview.png',
    url = window?.location?.href || 'https://sighttrails.com',
    type = 'website',
    structuredData
}) => {
    const siteTitle = 'SightTrails Safaris';
    const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;

    // Ensure absolute URL for image
    const getAbsoluteUrl = (path: string) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sighttrails.com';
        return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const imageUrl = getAbsoluteUrl(image);

    // Construct the base Organization Schema for the generic brand identity
    const baseSchema = {
        '@context': 'https://schema.org',
        '@type': 'TravelAgency',
        name: 'SightTrail Safaris',
        url: 'https://sighttrails.com',
        logo: 'https://sighttrails.com/SightTrails_Safaris_Logo_3-removebg-preview.png',
        image: 'https://sighttrails.com/SightTrails_Safaris_Logo_3-removebg-preview.png',
        description: 'SightTrail Safaris offers premium, authentic East African safari experiences guided by professional safari architects.',
        address: {
            '@type': 'PostalAddress',
            addressLocality: 'Nairobi',
            addressCountry: 'KE'
        },
        sameAs: [
            'https://www.facebook.com/profile.php?id=100089437171141',
            'https://www.instagram.com/sighttrails_safaris/',
            'https://www.tiktok.com/@sighttrails_safaris'
        ]
    };

    // Merge schemas
    const schemaArray = structuredData ? [baseSchema, structuredData] : [baseSchema];

    return (
        <Helmet>
            {/* Standard Metadata */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords.join(', ')} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="SightTrail Safaris" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={url} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />

            {/* JSON-LD Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(schemaArray)}
            </script>
        </Helmet>
    );
};

export default SEO;
